import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  MapPin, 
  X, 
  Check
} from 'lucide-react';
import { eventPackagesData } from '../utils/eventPackagesData';
import SEO from '../components/SEO';
import '../styles/EventPackages.css';

// --- Data ---
const ADD_ONS = [
  { 
    id: 'light', 
    name: 'Godox Light Kit', 
    price: 1500, 
    image: 'https://images.unsplash.com/photo-1624823183488-842279bb2223?auto=format&fit=crop&q=80&w=200'
  },
  { 
    id: 'gimbal', 
    name: 'Ronin Stabilizer', 
    price: 2000, 
    image: 'https://images.unsplash.com/photo-1554559281-9b6279932062?auto=format&fit=crop&q=80&w=200'
  },
  { 
    id: 'lens', 
    name: '85mm Prime Lens', 
    price: 1200, 
    image: 'https://images.unsplash.com/photo-1617005082133-548c4dd27f35?auto=format&fit=crop&q=80&w=200'
  },
  { 
    id: 'smoke', 
    name: 'Atmosphere Fog', 
    price: 800, 
    image: 'https://images.unsplash.com/photo-1505322969963-f9ce802871b6?auto=format&fit=crop&q=80&w=200'
  },
];

// Transform eventPackagesData to match the component structure
const transformPackage = (pkg) => {
  // Extract hours from duration string (e.g., "4 Hours" -> 4, "Full Day" -> 8, "Flexible" -> 4)
  let baseHours = 4;
  if (pkg.duration.includes('Full Day')) {
    baseHours = 8;
  } else if (pkg.duration === 'Flexible') {
    baseHours = 4;
  } else {
    const hoursMatch = pkg.duration.match(/(\d+)/);
    if (hoursMatch) {
      baseHours = parseInt(hoursMatch[1]);
    }
  }

  // Calculate overtime rate (10% of base price per hour)
  const overtimeRate = Math.round(pkg.price * 0.1);

  // Handle image path
  let imageUrl = pkg.image;
  if (!imageUrl.startsWith('http') && !imageUrl.startsWith('/')) {
    imageUrl = '/' + imageUrl;
  }

  return {
    id: pkg.id,
    title: pkg.name,
    price: pkg.price,
    image: imageUrl,
    description: pkg.fullDescription,
    duration: pkg.duration,
    baseHours: baseHours,
    overtimeRate: overtimeRate,
    features: pkg.features
  };
};

// --- Component ---
const Modal = ({ pkg, onClose }) => {
  const [selectedAddons, setSelectedAddons] = useState([]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  const toggleAddon = (id) => {
    if (selectedAddons.includes(id)) {
      setSelectedAddons(prev => prev.filter(item => item !== id));
    } else {
      setSelectedAddons(prev => [...prev, id]);
    }
  };

  const calculateTotal = () => {
    let total = pkg.price;
    selectedAddons.forEach(id => {
      const addon = ADD_ONS.find(a => a.id === id);
      if (addon) total += addon.price;
    });
    return total;
  };

  const handleRequestBooking = () => {
    let phoneNumber = process.env.REACT_APP_WHATSAPP || process.env.REACT_APP_PHONE_NUMBER || '';
    
    // Format phone number for WhatsApp (remove +, spaces, and other non-numeric characters except country code)
    phoneNumber = phoneNumber.replace(/[^\d]/g, '');
    
    // Build the message with package details
    let message = `Hello! I'm interested in booking the *${pkg.title}* package.\n\n`;
    
    message += `*Package Details:*\n`;
    message += `📦 Package: ${pkg.title}\n`;
    message += `💰 Base Price: ₹${pkg.price.toLocaleString()}\n`;
    message += `⏱️ Duration: ${pkg.duration}\n`;
    message += `💵 Overtime Rate: ₹${pkg.overtimeRate}/hr\n\n`;
    
    if (selectedAddons.length > 0) {
      message += `*Selected Add-ons:*\n`;
      selectedAddons.forEach(id => {
        const addon = ADD_ONS.find(a => a.id === id);
        if (addon) {
          message += `• ${addon.name} - ₹${addon.price.toLocaleString()}\n`;
        }
      });
      message += `\n`;
    }
    
    message += `*Total Estimated Investment: ₹${calculateTotal().toLocaleString()}*\n\n`;
    
    message += `*Included Services:*\n`;
    pkg.features.slice(0, 5).forEach(feature => {
      message += `✓ ${feature}\n`;
    });
    if (pkg.features.length > 5) {
      message += `... and ${pkg.features.length - 5} more\n`;
    }
    
    message += `\nPlease let me know about availability and booking process.`;
    
    // Encode the message for URL
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    
    // Open WhatsApp in a new tab
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="ep-modal-overlay" onClick={onClose}>
      <div className="ep-modal-container" onClick={e => e.stopPropagation()}>
        <button className="ep-modal-close-btn" onClick={onClose}>
          <X size={32} strokeWidth={1} />
        </button>
        <div className="ep-modal-image-side">
          <img src={pkg.image} alt={pkg.title} />
        </div>
        <div className="ep-modal-content-side">
          <div className="ep-modal-header">
            <h2>{pkg.title}</h2>
            <span className="ep-modal-price">Starting at ₹{pkg.price.toLocaleString()}</span>
          </div>
          <span className="ep-section-label">Included Services</span>
          <div className="ep-features-list">
            {pkg.features.map((f, i) => (
              <div key={i} className="ep-feature-item">
                <Check size={16} color="#B08968" /> {f}
              </div>
            ))}
          </div>
          <div className="ep-divider"></div>
          <span className="ep-section-label">Curated Add-ons</span>
          <div className="ep-addons-container">
            {ADD_ONS.map(addon => {
              const isSelected = selectedAddons.includes(addon.id);
              return (
                <div 
                  key={addon.id} 
                  className={`ep-addon-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => toggleAddon(addon.id)}
                >
                  <img src={addon.image} alt={addon.name} className="ep-addon-img" />
                  <span className="ep-addon-name">{addon.name}</span>
                  {isSelected && <Check size={16} color="#B08968" style={{marginLeft: 'auto'}} />}
                </div>
              );
            })}
          </div>
          <div className="ep-info-bar">
            <span className="ep-info-label"><Clock size={16} /> Duration</span>
            <span className="ep-info-value">{pkg.duration} (Overtime: ₹{pkg.overtimeRate}/hr)</span>
          </div>
          <div className="ep-info-bar">
            <span className="ep-info-label"><MapPin size={16} /> Travel Policy</span>
            <span className="ep-info-value">Free travel charges within 5km</span>
          </div>
          <div className="ep-modal-footer">
            <div className="ep-total-display">
              <span className="ep-total-label">Estimated Investment</span>
              <span className="ep-total-amount">₹{calculateTotal().toLocaleString()}</span>
            </div>
            <button className="ep-book-btn" onClick={handleRequestBooking}>Request Booking</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const EventPackages = () => {
  const [activePackage, setActivePackage] = useState(null);


  const packages = eventPackagesData.map(transformPackage);

  return (
    <>
      <SEO 
        title="Event Packages - Professional Photography & Videography | BookMyCamera"
        description="Professional event photography packages in Bangalore. Wedding photography, pre-wedding shoots, post-wedding photography, and complete event coverage packages. Book your perfect photography package today."
        keywords="event packages bangalore, wedding photography packages, pre-wedding photography bangalore, post-wedding photography, event photography packages, photography services bangalore, wedding shoot packages, event coverage bangalore"
      />
      <div className="container">
        <header className="hero">
          <h1>Capture Life's Moments</h1>
          <p>Curated photography experiences designed for the modern storyteller.</p>
        </header>
        <div className="grid">
          {packages.map(pkg => (
            <div key={pkg.id} className="package-card" onClick={() => setActivePackage(pkg)}>
              <div className="card-image-container">
                <img src={pkg.image} alt={pkg.title} className="card-image" />
              </div>
              <div className="card-content">
                <h3 className="card-title">{pkg.title}</h3>
                <span className="card-price">From ₹{pkg.price.toLocaleString()}</span>
                
                <div className="features-preview">
                  {pkg.features.slice(0, 2).map((f, i) => (
                    <span key={i}>{f}</span>
                  ))}
                </div>
                <button className="btn-view">View Details</button>
              </div>
            </div>
          ))}
        </div>
        {activePackage && (
          <Modal pkg={activePackage} onClose={() => setActivePackage(null)} />
        )}
      </div>
    </>
  );
};

export default EventPackages;
